// Every button in Cockpit that sends work into the agent queue, and the prompt (the job's queue_note) it writes.
// Agents → Prompts reads this: each entry shows where the button lives, what it creates, the skill the note routes
// the agent to and the latest real prompt it sent (matched on the job row). Entries with a `prompt` are editable
// there: the button renders its note from the newest saved template (public.agent_prompts) or the built-in default
// (src/lib/promptTemplates.mjs), filling the variables listed here. `sample` renders any template with made-up
// data through the button's own builder, which is how the editor previews a draft. Add an entry when you add a
// queue button.

import { formatDensityQueueNote, formatLinksQueueNote, formatRetroOptQueueNote, formatTemplateQueueNote } from './retroOpt';
import { formatOfferIdeationNote } from './offers';
import { formatBuildInstruction, formatPageBuildInstruction, formatSweepQueueNote, type ContentQueueRow } from './keywordSweep';
import { formatContentQueueNote } from './contentJobs';
import { formatIdeationQueueNote } from './ideationJobs';
import { formatFixQueueNote, type FixItem } from './hostingFixPlan';
import { formatSetupQueueNote } from './hostingSetup';
import { formatSecurityAuditNote } from './securityAudits';
import { formatFeedbackQueueNote, type FeedbackBatch } from './clientFeedback';
import type { HostedSite, HostingRun } from './hosting';
import { PROMPT_DEFAULTS, pageSectionVars, renderPrompt, type PromptId } from './prompts';
import { PAGE_BUILD_SUMMARY } from '../components/ai/operator/fixtures/pageBuild';
import type { AiJob, TaskTypeId } from './supabase.types';

const SAMPLE_FEEDBACK = {
  id: '00000000-batch',
  client: { name: "Pete's Panels" },
  source: { id: 's', key: 'petes-panels', client_id: 'c', label: "Pete's Panels CRM", repo: 'eczzz/Petes-Panels-backend', repo_branch: 'main', db_ref: 'vekkptpabhizqybjvydr', preview_db_ref: 'djhuewpximhwyarlchhh', netlify_site: 'petes-panels-backend' },
  sender: { full_name: 'Pete Ortega' },
  note: 'Couple things from this morning',
  items: [
    {
      id: '00000000-note1', kind: 'bug', origin: 'app', message: 'Estimate approved but it never turned into an invoice',
      page_url: 'https://admin.petespanels.com/project/00000000', page_title: 'PP-18525 · Red Rocks', route: '/project/00000000',
      record: { type: 'project', id: '00000000', refs: ['PP-18525', 'Estimate 8715'] },
      element: { tag: 'span', text: 'Approved', selector: 'div.qb-block > span.badge', heading: 'QuickBooks', nearby: ['Convert to invoice', 'Send estimate'] },
      click: { xPct: 61.2, yPct: 34.5, viewport: { w: 1440, h: 900 } },
      errors: [], network: [{ at: '', method: 'POST', url: '/functions/v1/qbo-sync', status: 500, detail: 'Invoice already exists' }],
      app_version: 'a591c27', screenshot_path: '00000000-batch/00000000-note1/full.jpg', crop_path: '00000000-batch/00000000-note1/pin.jpg',
    },
  ],
} as unknown as FeedbackBatch;

export interface QueueEntryPoint {
  id: string;
  label: string;
  /** Where the button is, as a person would find it. */
  where: string;
  kind: AiJob['kind'];
  taskType: TaskTypeId;
  /** The workspace skill the prompt points the agent at (skills/<name>/SKILL.md), or how the note works without one. */
  skill: string;
  /** Does this job row come from this button? Checked in list order; the last entry catches the rest. */
  match: (job: Pick<AiJob, 'kind' | 'title' | 'brief'>) => boolean;
  /** The editable prompt behind the button: its id in agent_prompts and the variables the button fills, name → what it holds. */
  prompt?: { id: PromptId; vars: Record<string, string> };
  /** The prompt rendered with made-up data through the real builder; pass a template to preview a draft. */
  sample?: (template?: string) => string;
  /** A prompt an agent renders mid-job (cockpit ai section-prompt), not a button that queues a job. */
  buildStep?: boolean;
}

const has = (brief: unknown, key: string) => !!brief && typeof brief === 'object' && key in (brief as Record<string, unknown>);
const SAMPLE_BASELINE = { window: '2026-09-08..2026-10-05', clicks: 3, impressions: 288, ctr: 1, position: 26.4, queries: 59 };
const SAMPLE_URL = 'https://bdtelectric.com/emergency-electrician-langley/';
const SAMPLE_OFFER = {
  name: '24/7 emergency electrical repair',
  include_ideas: 'A licensed electrician on site within the hour\nThe call-out fee is quoted upfront, before anyone drives over\nFixed the same night, not patched',
  exclude_ideas: 'DIY safety tips\nScare tactics about house fires',
};

const PAGE_VARS = {
  client: 'Client name',
  page_url: 'The page’s live URL',
  page_id: 'Cockpit page id (client_pages)',
  why: 'The note typed next to the button, or "(no note)"',
  search_console: 'One line: clicks, impressions, CTR, position and queries over the last 28 days when queued',
};

const SAMPLE_RUN = { id: '00000000-run', created_at: '2026-10-08T14:02:00Z', finished_at: '2026-10-08T14:06:00Z' } as unknown as HostingRun;
const SAMPLE_FIXES = [
  { urgency: 'high', title: 'Astro 4.16 has a published XSS advisory', action: 'Upgrade Astro to the patched release and rebuild; compare the preview page by page.' },
  { urgency: 'medium', title: 'No Content-Security-Policy header', action: 'Add a report-only CSP in netlify.toml that matches the scripts the site loads.' },
] as unknown as FixItem[];
const SAMPLE_PERSON = [{ title: 'Renew the domain before Nov 2 (registrar login is the client’s)' }] as unknown as FixItem[];
const HOSTING_FIX_VARS = {
  client: 'Client name (the hosting account’s name when the account has no client)',
  account: 'Hosting account name',
  site_url: 'The site’s URL',
  run_when: 'When the hosting checklist run finished',
  run_id: 'The hosting checklist run id',
  fixes: 'Numbered list of the findings the agent fixes: [urgency] title, then the action',
  not_yours: 'Findings a person handles, with their heading; empty when there are none',
};

export const QUEUE_ENTRY_POINTS: QueueEntryPoint[] = [
  {
    id: 'retro-opt',
    label: 'Retroactive Optimization',
    where: 'Client detail → Pages → a page → Journal',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'retro-opt',
    match: (j) => has(j.brief, 'retro_opt'),
    prompt: { id: 'retro-opt', vars: { ...PAGE_VARS, primary_phrase: 'The phrase typed in, or an instruction to pick it from Search Console' } },
    sample: (template) =>
      formatRetroOptQueueNote(
        'BDT Electric',
        { page_id: '00000000-sample', url: SAMPLE_URL, primary_phrase: 'emergency electrician langley', note: 'Dropped after the September update.', baseline: SAMPLE_BASELINE },
        template,
      ),
  },
  {
    id: 'density-fix',
    label: 'Keyword Density Fix',
    where: 'Client detail → Pages → a page → Journal',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'keyword-density-fix',
    match: (j) => has(j.brief, 'density_fix') || has(j.brief, 'overopt_fix'),
    prompt: { id: 'density-fix', vars: { ...PAGE_VARS, phrases: 'One line per chosen phrase: OVER/UNDER/IN LINE, uses, density, headings, where it appears, rank' } },
    sample: (template) =>
      formatDensityQueueNote(
        'BDT Electric',
        {
          page_id: '00000000-sample',
          url: SAMPLE_URL,
          phrases: [
            { phrase: 'langley', uses: 17, density: 2.13, headings: 5, heading_share: 0.71, in_url: true, in_h1: true, in_title: true, impressions: null, position: null, level: 'over', sources: ['url'] },
            { phrase: 'emergency electrician langley', target: true, uses: 1, density: 0.38, headings: 3, heading_share: 0.43, in_url: true, in_h1: true, in_title: true, impressions: 44, position: 24.1, level: 'under', sources: ['query', 'url'] },
          ],
          note: '',
          baseline: SAMPLE_BASELINE,
        },
        7,
        template,
      ),
  },
  {
    id: 'links-fix',
    label: 'Internal Links Fix',
    where: 'Client detail → Pages → a page → Journal',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'internal-links-fix',
    match: (j) => has(j.brief, 'links_fix'),
    prompt: { id: 'links-fix', vars: { ...PAGE_VARS, inlinks: 'Internal links pointing at the page when queued (Ahrefs crawl)' } },
    sample: (template) =>
      formatLinksQueueNote('BDT Electric', { page_id: '00000000-sample', url: SAMPLE_URL, inlinks: 2, note: '', baseline: SAMPLE_BASELINE }, template),
  },
  {
    id: 'template-fix',
    label: 'Templated Content Fix',
    where: 'Client detail → Pages → a page → Journal',
    kind: 'fulfillment',
    taskType: 'page_build',
    skill: 'templated-content-fix',
    match: (j) => has(j.brief, 'template_fix'),
    prompt: {
      id: 'template-fix',
      vars: { ...PAGE_VARS, share: 'How much of the page is on its closest sibling, e.g. 80%', siblings: 'One line per sibling over 20%: URL — share' },
    },
    sample: (template) =>
      formatTemplateQueueNote(
        'BDT Electric',
        {
          page_id: '00000000-sample',
          url: SAMPLE_URL,
          share: 0.8,
          siblings: [
            { url: 'https://bdtelectric.com/emergency-electrician-hope/', share: 0.8 },
            { url: 'https://bdtelectric.com/emergency-electrician-mission/', share: 0.61 },
          ],
          note: '',
          baseline: SAMPLE_BASELINE,
        },
        template,
      ),
  },
  {
    id: 'offer-ideation',
    label: 'Ideate offers',
    where: 'Client detail → Offers',
    kind: 'offers',
    taskType: 'general',
    skill: 'offer-ideation',
    match: (j) => j.kind === 'offers',
    prompt: {
      id: 'offer-ideation',
      vars: {
        client: 'Client name',
        client_id: 'Cockpit client id',
        competencies: 'How many competencies are on file',
        offers: 'How many offers exist (any status)',
        steer: 'The note typed next to the button, or "(none)"',
      },
    },
    sample: (template) => formatOfferIdeationNote('BDT Electric', '00000000-sample', 'Focus on residential; no commercial work.', { competencies: 12, offers: 3 }, template),
  },
  {
    id: 'google-ads-checkin',
    label: 'Account Check-in (and the Sunday-night schedule)',
    where: 'Client detail → Google Ads → Account Check-in, and every Sunday night for each linked account',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'google-ads-checkin',
    match: (j) => has(j.brief, 'ads_checkin'),
    prompt: {
      id: 'google-ads-checkin',
      vars: {
        client: 'Client name',
        client_id: 'Cockpit client id',
        customer_id: 'Google Ads customer id (10 digits)',
        leads: 'The conversion actions counted as leads, by name and id',
        trigger: 'Who queued it: the person (button) or the weekly schedule',
      },
    },
    sample: (template) =>
      renderPrompt(template ?? PROMPT_DEFAULTS['google-ads-checkin'], {
        client: 'The Reputation Law Group',
        client_id: '00000000-sample',
        customer_id: '4449048007',
        leads: 'Page view (Page load thank-you) (7528094610) + Calls from ads (7531722429)',
        trigger: 'Kyle (Account Check-in button)',
      }),
  },
  {
    id: 'keyword-sweep',
    label: 'Run sweep now (and the monthly schedule)',
    where: 'Client detail → Content Queue → Keyword sweep, and the monthly sweep on each client’s sweep day',
    kind: 'sweep',
    taskType: 'general',
    skill: 'none: the note is the whole procedure',
    match: (j) => j.kind === 'sweep',
    prompt: { id: 'keyword-sweep', vars: { client: 'Client name', run_id: 'The seo_sweep_runs id the agent works' } },
    sample: (template) => formatSweepQueueNote({ client_id: '00000000-sample', client_name: 'BDT Electric', run_id: '00000000-run' }, template),
  },
  {
    id: 'send-to-build-page',
    label: 'Send to build queue (new landing page)',
    where: 'Client detail → Content Queue → an approved new landing page',
    kind: 'fulfillment',
    taskType: 'page_build',
    skill: 'page-build',
    match: (j) => has(j.brief, 'page_build'),
    prompt: {
      id: 'send-to-build-page',
      vars: {
        client: 'Client name',
        keyword: 'The row’s target keyword: the page’s given keyword',
        secondary: 'Up to four cluster terms, comma separated, or a note that the cluster has none',
        target: 'The keyword in quotes, its intent, volume, KD and the site’s DR',
        intended_url: 'The proposed URL, marked new page',
        context: 'Why this tier, the pitch and the SERP note, one per line; empty when there are none',
        repo: 'The repo line: the one repo, the repos to pick from, or "no repo on file"',
        row_id: 'The Content Queue row id',
      },
    },
    sample: (template) =>
      formatPageBuildInstruction({
        row: {
          id: '00000000-row',
          title: 'Emergency electrician Langley',
          target_keyword: 'emergency electrician langley',
          content_type: 'landing_page',
          volume: 210,
          difficulty: 4,
          recommended_url: null,
          brief: {
            cluster: ['emergency electrician langley', '24 hour electrician langley', 'electrician near me langley'],
            intents: ['local', 'transactional'],
            intended_url: '/emergency-electrician-langley/',
            is_new_page: true,
            dr: 11,
            kd_cap: 12,
            tier_reason: 'commercial local intent for a service they sell',
            pitch: 'A Langley page that answers "can you come tonight" in the first screen. Their call-out work is their best lead source.',
          },
        } as unknown as ContentQueueRow,
        clientName: 'BDT Electric',
        repos: [{ name: 'bdt-astro', repo_url: 'https://github.com/eczzz/bdt-astro' }],
        template,
      }),
  },
  {
    id: 'send-to-build',
    label: 'Send to build queue (optimization or tool)',
    where: 'Client detail → Content Queue → an approved optimization or tool (new landing pages before 2026-10-09 too)',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'agent-queues (spec)',
    match: (j) => /^(Landing page|Optimize|Tool) — /.test(j.title ?? ''),
    prompt: {
      id: 'send-to-build',
      vars: {
        what: '"a new landing page", "an optimization of <url>" or "an interactive tool"',
        client: 'Client name',
        target: 'The keyword in quotes, its cluster, intent, volume, KD and the site’s DR',
        intended_url: 'The URL, and whether it is new or already ranking',
        context: 'Why this tier, the pitch and the SERP note, one per line; empty when there are none',
        type_item: 'Item 8 for optimizations and tools; empty for a new landing page',
        repo: 'The repo line: the one repo, the repos to pick from, or "no repo on file"',
        row_id: 'The Content Queue row id',
      },
    },
    sample: (template) =>
      formatBuildInstruction({
        row: {
          id: '00000000-row',
          title: 'Emergency electrician Langley',
          target_keyword: 'emergency electrician langley',
          content_type: 'landing_page_optimise',
          volume: 210,
          difficulty: 4,
          recommended_url: 'https://bdtelectric.com/electrician-langley/',
          brief: {
            cluster: ['emergency electrician langley', '24 hour electrician langley', 'electrician near me langley'],
            intents: ['local', 'transactional'],
            intended_url: 'https://bdtelectric.com/electrician-langley/',
            is_new_page: false,
            position_now: 14,
            dr: 11,
            kd_cap: 12,
            tier_reason: 'commercial local intent for a service they sell',
            pitch: 'A Langley page that answers "can you come tonight" in the first screen. Their call-out work is their best lead source.',
          },
        } as unknown as ContentQueueRow,
        clientName: 'BDT Electric',
        repos: [{ name: 'bdt-astro', repo_url: 'https://github.com/eczzz/bdt-astro' }],
        template,
      }),
  },
  {
    id: 'send-to-writer',
    label: 'Send to writer',
    where: 'Client detail → Content Queue → an approved article, and Content → queue a brief',
    kind: 'content',
    taskType: 'general',
    skill: 'content-article',
    match: (j) => j.kind === 'content',
    prompt: {
      id: 'send-to-writer',
      vars: {
        primary: 'The primary search phrase',
        secondary: 'Secondary phrases, comma separated; empty when there are none',
        repo: 'The repo, or "none — article lands in Content → Pieces"',
        attachments: 'Attached files with their heading; empty when there are none',
      },
    },
    sample: (template) =>
      formatContentQueueNote(
        {
          title: 'How much does a panel upgrade cost in Langley?',
          primary: 'panel upgrade cost langley',
          secondary: ['200 amp panel upgrade', 'electrical panel replacement bc'],
          client_id: '00000000-sample',
          client_name: 'BDT Electric',
          repo_id: null,
          repo_name: null,
          repo_url: null,
          attachments: [],
        },
        template,
      ),
  },
  {
    id: 'seo-diagnosis',
    label: 'Diagnose',
    where: 'Ideation → findings',
    kind: 'ideation',
    taskType: 'general',
    skill: 'seo-diagnosis',
    match: (j) => j.kind === 'ideation',
    prompt: {
      id: 'seo-diagnosis',
      vars: {
        client: 'Client name',
        findings: 'One line per selected finding: [kind] "query" → URL · position · impressions · clicks/mo if fixed',
        opportunity_ids: 'The finding ids, comma separated',
      },
    },
    sample: (template) =>
      formatIdeationQueueNote(
        {
          client_id: '00000000-sample',
          client_name: 'BDT Electric',
          opportunity_ids: ['00000000-a', '00000000-b'],
          findings: [
            { id: '00000000-a', kind: 'ctr', query: 'electrician langley', url: 'https://bdtelectric.com/', position: 4.2, impressions: 1830, est_click_gain: 21 },
            { id: '00000000-b', kind: 'striking', query: 'ev charger installation langley', url: 'https://bdtelectric.com/ev-chargers/', position: 11.6, impressions: 640, est_click_gain: 9 },
          ],
        },
        template,
      ),
  },
  {
    id: 'hosting-fix-netlify',
    label: 'Send N fixes to the agent queue (Netlify site)',
    where: 'Hosting → an account → Fix plan, or Send fix on the Hosting table row, for a site with a repo',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'managed-hosting',
    match: (j) => (j.title ?? '').startsWith('Hosting fixes — ') && !(has(j.brief, 'hosting_fix') && (j.brief as { hosting_fix: { platform?: string } }).hosting_fix.platform === 'cloudways'),
    prompt: { id: 'hosting-fix-netlify', vars: { ...HOSTING_FIX_VARS, repo: 'owner/repo', branch: 'The branch production builds from', netlify_site: '" · Netlify site: <name>", or empty' } },
    sample: (template) =>
      formatFixQueueNote({
        account: { name: 'BDT Electric' },
        clientName: 'BDT Electric',
        run: SAMPLE_RUN,
        siteUrl: 'https://bdtelectric.com',
        target: { platform: 'netlify', repo: 'eczzz/bdt-astro', branch: 'main', netlify: 'bdt-electric' },
        agentItems: SAMPLE_FIXES,
        personItems: SAMPLE_PERSON,
        template,
      }),
  },
  {
    id: 'hosting-fix-wordpress',
    label: 'Send N fixes to the agent queue (WordPress site)',
    where: 'Hosting → an account → Fix plan, or Send fix on the Hosting table row, for a WordPress site on Cloudways',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'managed-hosting (WordPress sites)',
    match: (j) => (j.title ?? '').startsWith('Hosting fixes — '),
    prompt: { id: 'hosting-fix-wordpress', vars: { ...HOSTING_FIX_VARS, server_id: 'Cloudways server id', app_id: 'Cloudways app id (the runner’s site ref)' } },
    sample: (template) =>
      formatFixQueueNote({
        account: { name: 'Pete’s Panels' },
        clientName: 'Pete’s Panels',
        run: SAMPLE_RUN,
        siteUrl: 'https://petespanels.com',
        target: { platform: 'cloudways', server_id: '1234567', app_id: '7654321' },
        agentItems: [
          { urgency: 'high', title: '6 plugins have updates, 1 with a security fix', action: 'Snapshot, then update them one at a time and screenshot the key pages after each.' },
        ] as unknown as FixItem[],
        personItems: [],
        template,
      }),
  },
  {
    id: 'hosting-setup',
    label: 'Set up with agent',
    where: 'Hosting → an account → a site not set up yet',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'managed-hosting (Set up a site)',
    match: (j) => (j.title ?? '').startsWith('Hosting setup — '),
    prompt: {
      id: 'hosting-setup',
      vars: {
        account: 'Hosting account name',
        site_name: 'Site name',
        site_url: 'Site URL',
        site_id: 'Cockpit monitored_sites id',
        platform: '"WordPress on Cloudways", "Netlify" or "not linked to Cloudways or Netlify yet"',
      },
    },
    sample: (template) =>
      formatSetupQueueNote(
        { name: 'BDT Electric' },
        { id: '00000000-site', name: 'BDT Electric', url: 'https://bdtelectric.com', netlify_site_id: '0000-netlify', cloudways_app_id: null } as unknown as HostedSite,
        template,
      ),
  },
  {
    id: 'page-build-section',
    label: 'Page Build: build one section',
    where: 'Not a button: the build stage of every Page Build job, once per section, in page order (cockpit ai section-prompt)',
    kind: 'fulfillment',
    taskType: 'page_build',
    skill: 'page-build: the build agent hands this to a fresh agent per section',
    // Rendered by the build agent through the CLI, never written as a queue note, so no job matches it.
    match: () => false,
    buildStep: true,
    prompt: {
      id: 'page-build-section',
      vars: {
        client: 'Client name',
        page_url: 'The page being built',
        job_id: 'The Page Build job id',
        section_number: 'This section’s place on the page, from 1',
        section_count: 'How many sections the page has',
        heading: 'The section’s working heading',
        level: 'Its heading level: 1 for the H1, else 2 or 3',
        purpose: 'What the section is',
        message: 'What it has to get across',
        source: 'Source material to include as given, with its heading; empty when there is none',
        image: 'The image instruction: what to generate and how, or "no generated image" for this section',
        outline: 'Every section in page order, one line each, this one marked ← YOU',
        keyword: 'The page’s target search and how to use it; empty when the spec has none',
        buyer: 'The spec’s buyer brief: who, what they want, what they know, what holds them back, the promise, the action',
        offer: 'The offer the page sells (Client detail → Offers): its name, ideas to include and ideas to leave out',
        signature: 'The motion instruction: the signature-moment brief on the page’s one or two signature sections, restraint everywhere else',
      },
    },
    sample: (template) => {
      const sections = PAGE_BUILD_SUMMARY.spec?.sections ?? [];
      const keyword = PAGE_BUILD_SUMMARY.spec?.keyword ?? null;
      const buyer = PAGE_BUILD_SUMMARY.spec?.buyer ?? null;
      return renderPrompt(
        template ?? PROMPT_DEFAULTS['page-build-section'],
        pageSectionVars({ client: 'BDT Electric', pageUrl: 'https://bdtelectric.com/emergency-electrician-langley/', jobId: '00000000-sample', sections, index: 0, keyword, buyer, offer: SAMPLE_OFFER }),
      );
    },
  },
  {
    id: 'security-audit',
    label: 'Audit',
    where: 'Security → Audits → a system (one open audit per system)',
    kind: 'fulfillment',
    taskType: 'security_audit',
    skill: 'security-audit',
    match: (j) => has(j.brief, 'security_system') || (j.title ?? '').startsWith('Security Audit — '),
    prompt: {
      id: 'security-audit',
      vars: {
        system: 'The system’s name (Supabase project, else the repo)',
        system_id: 'Cockpit security_systems id (cockpit security calls <id> lists its saved cascade calls)',
        kind: '"database system" or "static: no database"',
        client: 'Client name; empty when the system has none',
        database: 'Supabase ref and org, flagged when our management token can’t reach it; "none" for static systems',
        repos: 'One line per repo in the system',
        sites: 'One line per Netlify site: name and live URL',
        cascade_calls: 'How many cascade calls are saved for the system, or "none yet"',
      },
    },
    sample: (template) =>
      formatSecurityAuditNote(
        {
          id: '00000000-system',
          key: 'db:echldzywqxjgpgwcrdqc',
          name: 'AAML',
          kind: 'database',
          supabase_ref: 'echldzywqxjgpgwcrdqc',
          supabase_org: 'bullfinch',
          reachable: true,
          repos: ['eczzz/AAML-backend-app', 'eczzz/AAML-colorado-chapter'],
          sites: [
            { id: '0000-a', name: 'aaml-colorado', url: 'https://aaml-colorado.netlify.app', domains: ['aamlcolorado.org'] },
            { id: '0000-b', name: 'aaml-backend', url: 'https://aaml-backend.netlify.app', domains: [] },
          ],
          client_name: 'AAML',
          notes: null,
        },
        3,
        template,
      ),
  },
  {
    id: 'client-feedback',
    label: 'Send to agent queue (feedback batch)',
    where: 'Comms → Feedback → a batch of notes a client pinned in feedback mode',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'agent-queues: one spec for the batch, every note answered or built; questions answered in the spec',
    match: (j) => typeof (j.brief as { feedback_batch_id?: unknown } | null)?.feedback_batch_id === 'string',
    prompt: {
      id: 'client-feedback',
      vars: {
        client: 'Client name',
        count: 'How many notes are in the batch',
        sender: 'Who pinned them',
        source: 'The app they were pinned in',
        batch_note: 'What the client wrote about the whole batch (line dropped when empty)',
        batch_id: 'client_feedback_batches id',
        notes: 'One block per note: words, page, record, pinned element and its neighbours, errors, screenshots',
      },
    },
    sample: (template) => formatFeedbackQueueNote(SAMPLE_FEEDBACK, template),
  },
  {
    id: 'spec-queue',
    label: 'Send to agent queue',
    where: 'A task (client tasks, Fulfillment) or a comm event: the human writes the note',
    kind: 'fulfillment',
    taskType: 'general',
    skill: 'agent-queues: no canned prompt, the note is what the person typed (optionally started from Agents → Note templates) plus attachments',
    match: () => true,
  },
];

export const entryPointFor = (job: Pick<AiJob, 'kind' | 'title' | 'brief'>): QueueEntryPoint =>
  QUEUE_ENTRY_POINTS.find((e) => e.match(job)) ?? QUEUE_ENTRY_POINTS[QUEUE_ENTRY_POINTS.length - 1];
